import Image from '@tiptap/extension-image';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef, useState } from 'react';
import type {
  AttachmentLink,
  AttachmentLinkPolicy,
  MessageSummary,
  OutgoingAttachment,
  RecentPerson,
  SendAsAddress,
  SendRequest,
  StorageAccount,
  StorageEntry,
} from 'zitimail-shared-types';

import { api } from '../admin/api';
import { contacts } from '../api/calendar';
import { mail, uploadAttachmentLink, type LinkUpload } from '../api/mail';
import { fetchForAttachment, storage } from '../api/storage';
import { useToast } from '../components/Toasts';
import { FilePicker } from '../files/Files';
import { AiStrip, type ReplyContext } from './AiStrip';
import { recentPeople, seedFromSent } from './recentPeople';
import { checkDraft, DlpDialog, needsDecision, type DlpStop } from './DlpDialog';
import {
  BoldIcon,
  ClockIcon,
  CloseIcon,
  ExpandIcon,
  FilesIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListNumberIcon,
  MinimizeIcon,
  PaperclipIcon,
  QuoteIcon,
  SendIcon,
  TrashIcon,
  UnderlineIcon,
} from '../components/icons';
import { htmlToText, size } from './format';
import { newContentId, Pictures, PICTURE_TYPES, type InlineImage } from './inlineImages';

/** Thunderbird's templates folder, shared with it (Phase 11). */
const TEMPLATES = 'Templates';

export interface Draft {
  to: string;
  cc: string;
  bcc?: string;
  subject: string;
  /** Plain text, for drafts that start from text (Contacts, a mailto). */
  text: string;
  /** The starting body; wins over `text`. */
  html?: string;
  inReplyTo?: string;
  references?: string[];
  /** The saved draft this continues, in the Drafts folder. */
  draftUid?: string;
  /** Files already attached — a reopened draft's. */
  attachments?: Array<OutgoingAttachment & { size: number }>;
  /** Pictures in the body — a reopened draft's, or the message Undo took back. */
  images?: readonly InlineImage[];
  /** The message a reply answers, for the writing assistant. */
  original?: ReplyContext;
}

/** A reopened draft's files: its attachments, and the pictures in its body. */
export function draftFiles(
  files: ReadonlyArray<OutgoingAttachment & { size: number }>,
): Pick<Draft, 'attachments' | 'images'> {
  return {
    attachments: files.filter((f) => !f.contentId),
    images: files.flatMap((f) => (f.contentId ? [{ ...f, contentId: f.contentId }] : [])),
  };
}

const MAX_ATTACHMENTS = 25 * 1024 * 1024;

/** The class on a download link the composer put in the body (PRD Phase 5.4). */
const FILE_LINK = 'file-link';

const WAIT = 'Wait for the upload to finish, or cancel it.';

/** A large file on its way to becoming a download link. */
interface Upload {
  readonly id: number;
  readonly filename: string;
  readonly size: number;
  /** 0 to 1. */
  readonly progress: number;
}

const split = (s: string) =>
  s
    .split(/[,;\s]+/)
    .map((a) => a.trim())
    .filter(Boolean);

const escape = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c);

/**
 * Where the person's own writing ends: the signature separator, a quoted
 * original (the "On … wrote:" line before it), or a forwarded message. A
 * draft from the writing assistant replaces what comes before it.
 */
function ownTextEnd(editor: Editor): { pos: number; text: string; bounded: boolean } {
  const { doc } = editor.state;
  let pos = 0;
  let text = '';
  for (let i = 0; i < doc.childCount; i += 1) {
    const node = doc.child(i);
    const next = i + 1 < doc.childCount ? doc.child(i + 1) : null;
    const content = node.textContent;
    if (
      node.type.name === 'blockquote' ||
      content.trim() === '--' ||
      content.startsWith('---------- Forwarded message') ||
      next?.type.name === 'blockquote'
    ) {
      return { pos, text, bounded: true };
    }
    text += content;
    pos += node.nodeSize;
  }
  return { pos: doc.content.size, text, bounded: false };
}

/**
 * Where a download link goes: after the person's last words, above the
 * signature and any quoted original, so links added one after another stay
 * in order.
 */
function linkPosition(editor: Editor): number {
  const own = ownTextEnd(editor);
  const { doc } = editor.state;
  let pos = 0;
  let end = 0;
  for (let i = 0; i < doc.childCount && pos < own.pos; i += 1) {
    const node = doc.child(i);
    pos += node.nodeSize;
    if (node.textContent.trim()) {
      end = pos;
    }
  }
  return end;
}

/** The paragraph that stands in for a file sent as a link. */
export function linkHtml(link: AttachmentLink): string {
  const until = new Date(link.expires_at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    `<p>📎 <a class="${FILE_LINK}" href="${escape(link.url)}">${escape(link.filename)}</a>` +
    ` (${size(link.size)}) — download link, works until ${until}</p>`
  );
}

/**
 * The plain-text part. A download link carries its address, which HTML hides;
 * a picture is named where it was, as Outlook does.
 */
function plainText(editor: Editor): string {
  // The editor drops a paragraph's trailing space, but "-- " is the line
  // mail apps recognise a signature by.
  return editor
    .getText({
      blockSeparator: '\n\n',
      textSerializers: {
        image: ({ node }) => `[image: ${String(node.attrs.alt ?? '') || 'image'}]`,
        text: ({ node }) => {
          const link = node.marks.find(
            (m) => m.type.name === 'link' && m.attrs.class === FILE_LINK,
          );
          const text = node.text ?? '';
          return link ? `${text} <${String(link.attrs.href)}>` : text;
        },
      },
    })
    .replace(/^--$/m, '-- ');
}

/** Plain text as editor paragraphs. */
export function textToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** The signature block, separated the conventional way ("-- "). */
export function signatureHtml(signature: string | undefined): string {
  const value = signature?.trim();
  if (!value) {
    return '';
  }
  // A formatted signature is stored as HTML the server has already reduced
  // to formatting; an older one is plain text.
  const body = value.startsWith('<') ? value : `<p>${escape(value).replace(/\n/g, '<br>')}</p>`;
  return `<p></p><p>-- </p>${body}`;
}

function read(file: File): Promise<OutgoingAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve({
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        data: url.slice(url.indexOf(',') + 1),
      });
    };
    reader.onerror = () => reject(reader.error ?? new Error('could not read the file'));
    reader.readAsDataURL(file);
  });
}

function Toolbar({ editor }: { editor: Editor | null }): JSX.Element | null {
  if (!editor) {
    return null;
  }
  const tool = (
    label: string,
    Icon: (p: { className?: string }) => JSX.Element,
    active: boolean,
    run: () => void,
  ) => (
    <button
      type="button"
      className={`icon-btn${active ? ' on-tool' : ''}`}
      title={label}
      aria-label={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={run}
    >
      <Icon />
    </button>
  );
  return (
    <div className="editor-tools" role="toolbar" aria-label="Formatting">
      {tool('Bold', BoldIcon, editor.isActive('bold'), () =>
        editor.chain().focus().toggleBold().run(),
      )}
      {tool('Italic', ItalicIcon, editor.isActive('italic'), () =>
        editor.chain().focus().toggleItalic().run(),
      )}
      {tool('Underline', UnderlineIcon, editor.isActive('underline'), () =>
        editor.chain().focus().toggleUnderline().run(),
      )}
      <span className="bar-sep" />
      {tool('Bulleted list', ListIcon, editor.isActive('bulletList'), () =>
        editor.chain().focus().toggleBulletList().run(),
      )}
      {tool('Numbered list', ListNumberIcon, editor.isActive('orderedList'), () =>
        editor.chain().focus().toggleOrderedList().run(),
      )}
      {tool('Quote', QuoteIcon, editor.isActive('blockquote'), () =>
        editor.chain().focus().toggleBlockquote().run(),
      )}
      {tool('Link', LinkIcon, editor.isActive('link'), () => {
        const previous = (editor.getAttributes('link').href as string | undefined) ?? 'https://';
        const href = window.prompt('Link address', previous);
        if (href === null) {
          return;
        }
        if (!href.trim() || href === 'https://') {
          editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else if (/^(https?:|mailto:)/i.test(href.trim())) {
          editor.chain().focus().extendMarkRange('link').setLink({ href: href.trim() }).run();
        }
      })}
      <button
        type="button"
        className="text-btn tools-clear"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
      >
        Clear formatting
      </button>
    </div>
  );
}

type Suggestion = { name: string; email: string; source?: string };

/**
 * Saved contacts first, then the people written to before (most often and
 * most recently first), then the organisation's directory; each address once.
 */
export function mergeSuggestions(
  found: readonly Suggestion[],
  recent: readonly RecentPerson[],
): Suggestion[] {
  const seen = new Set<string>();
  const once = (s: Suggestion) => {
    const key = s.email.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  };
  const saved = found.filter((s) => s.source !== 'directory').filter(once);
  const written = recent
    .map((p): Suggestion => ({ name: p.name || p.address, email: p.address, source: 'recent' }))
    .filter(once);
  const directory = found.filter((s) => s.source === 'directory').filter(once);
  return [...saved, ...written, ...directory].slice(0, 12);
}

function AddressField({
  label,
  value,
  onChange,
  autoFocus,
  tenant,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
  /** For the people written to before; without it, contacts and directory only. */
  tenant?: string | undefined;
}): JSX.Element {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // Suggestions that arrive after the person has moved on to the next field
  // are not shown over it.
  const [focused, setFocused] = useState(false);
  const typing = value.split(/[,;]/).pop()?.trim() ?? '';
  useEffect(() => {
    if (typing.length < 1) {
      setSuggestions([]);
      return;
    }
    let live = true;
    const timer = window.setTimeout(() => {
      void Promise.all([
        contacts.search(typing).catch((): Suggestion[] => []),
        tenant
          ? recentPeople
              .search(tenant, typing)
              .then((r) => {
                if (!r.seeded) {
                  seedFromSent(tenant);
                }
                return r.people;
              })
              .catch((): RecentPerson[] => [])
          : Promise.resolve<RecentPerson[]>([]),
      ]).then(([found, recent]) => {
        if (live) {
          setSuggestions(mergeSuggestions(Array.isArray(found) ? found : [], recent));
        }
      });
    }, 180);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [typing, tenant]);
  return (
    <div className="cmp-field">
      <label>
        <span>{label}</span>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() =>
            window.setTimeout(() => {
              setFocused(false);
              setSuggestions([]);
            }, 150)
          }
          aria-label={label}
          autoComplete="off"
          autoFocus={autoFocus}
        />
      </label>
      {focused && suggestions.length > 0 && (
        <ul className="suggest" role="listbox" aria-label="Suggestions">
          {suggestions.map((s) => (
            <li key={s.email}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  // Replace the address being typed, keep the ones before it.
                  const done = value
                    .split(/[,;]/)
                    .slice(0, -1)
                    .map((a) => a.trim())
                    .filter(Boolean);
                  onChange([...done, s.email].join(', ') + ', ');
                  setSuggestions([]);
                }}
              >
                {s.name && s.name !== s.email ? (
                  <>
                    {s.name} <span className="muted">&lt;{s.email}&gt;</span>
                  </>
                ) : (
                  s.email
                )}
                {s.source === 'directory' && <span className="src">Your organisation</span>}
              </button>
              {s.source === 'recent' && tenant && (
                <button
                  type="button"
                  className="suggest-forget"
                  aria-label={`Stop suggesting ${s.email}`}
                  title="Stop suggesting this address"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    void recentPeople.forget(tenant, s.email).catch(() => undefined);
                    setSuggestions((all) => all.filter((x) => x.email !== s.email));
                  }}
                >
                  <CloseIcon />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A new message, reply or forward. Saves itself to Drafts as you write;
 * Send hands the message to the caller, which holds it for a few seconds so
 * it can still be undone.
 */
export function Compose({
  draft: initial,
  from,
  signature,
  tenant,
  senderName,
  plain = false,
  onClose,
  onSend,
  onSchedule,
}: {
  draft: Draft;
  from: string;
  signature?: string | undefined;
  /** Written as plain text: no formatting, sent without an HTML part. */
  plain?: boolean | undefined;
  /** The organisation, for the writing assistant; without it there is none. */
  tenant?: string | undefined;
  /** The person's name, for the assistant to sign drafts with. */
  senderName?: string | undefined;
  onClose: () => void;
  onSend: (request: SendRequest, reopen: Draft) => void;
  /** Send later: the message goes out at `at`, sent by the server. */
  onSchedule: (request: SendRequest, at: Date, reopen: Draft) => void;
}): JSX.Element {
  const [to, setTo] = useState(initial.to);
  const [cc, setCc] = useState(initial.cc);
  const [bcc, setBcc] = useState(initial.bcc ?? '');
  const [showCc, setShowCc] = useState(Boolean(initial.cc || initial.bcc));
  const [subject, setSubject] = useState(initial.subject);
  const [attachments, setAttachments] = useState<Array<OutgoingAttachment & { size: number }>>(
    initial.attachments ?? [],
  );
  const [draftUid, setDraftUid] = useState(initial.draftUid);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [mode, setMode] = useState<'normal' | 'min' | 'max'>('normal');
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(0);
  const [over, setOver] = useState(false);
  const files = useRef<HTMLInputElement>(null);
  // Large files go as download links when the organisation has them on.
  const [policy, setPolicy] = useState<AttachmentLinkPolicy | null>(null);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const running = useRef(new Map<number, LinkUpload>());
  const lastUpload = useRef(0);
  // Addresses an administrator lent this person (ADR-0045), offered in From.
  const [lent, setLent] = useState<readonly SendAsAddress[]>([]);
  const [sendAs, setSendAs] = useState<SendAsAddress | null>(null);

  const toast = useToast();
  // Pictures in the body, by the preview address the editor shows each by.
  const [pictures] = useState(() => new Pictures());
  // Files pasted or dropped into the body, handled with this render's state:
  // the editor keeps the props it was made with.
  const takeFiles = useRef<(files: File[], at: number | null, how: 'paste' | 'drop') => boolean>(
    () => false,
  );

  const start =
    initial.html ??
    (initial.text ? textToHtml(initial.text) : '<p></p>') + signatureHtml(signature);
  const [content] = useState(() =>
    plain
      ? `${start.startsWith('<p></p>') ? '<p></p>' : ''}${textToHtml(htmlToText(start))}`
      : pictures.localise(start, initial.images),
  );
  const editor = useEditor({
    extensions: [
      plain
        ? // Plain text: paragraphs, line breaks and links only, so a pasted
          // page arrives as its words, as it will be sent.
          StarterKit.configure({
            link: { openOnClick: false, autolink: true },
            bold: false,
            italic: false,
            underline: false,
            strike: false,
            code: false,
            codeBlock: false,
            heading: false,
            blockquote: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            horizontalRule: false,
          })
        : StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
      Placeholder.configure({ placeholder: 'Write your message' }),
      // Pictures sit in the text, as in Outlook; plain text has none.
      ...(plain ? [] : [Image.configure({ inline: true })]),
    ],
    content,
    editorProps: {
      // The browser's (or the operating system's) own spelling check, in the
      // languages the person has set up there.
      attributes: { spellcheck: 'true' },
      // A pasted page's own pictures (data:) get preview addresses too.
      transformPastedHTML: (html) => (plain ? html : pictures.localise(html)),
      handlePaste: (_view, event) => {
        const data = event.clipboardData;
        const files = Array.from(data?.files ?? []);
        // Word and Excel put a picture of the selection beside its text:
        // that pastes as the text.
        if (files.length === 0 || data?.getData('text/plain').trim()) {
          return false;
        }
        return takeFiles.current(files, null, 'paste');
      },
      handleDrop: (view, event, _slice, moved) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (moved || files.length === 0) {
          return false;
        }
        let at: number | null = null;
        try {
          at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null;
        } catch {
          at = null;
        }
        return takeFiles.current(files, at, 'drop');
      },
    },
    onUpdate: () => setDirty((n) => n + 1),
  });

  const request = (): SendRequest => {
    // The pictures go as inline parts, named by Content-ID; their preview
    // addresses never leave this page.
    const body = plain ? null : pictures.outgoing(editor?.getHTML() ?? '');
    return {
      to: split(to),
      cc: split(cc),
      bcc: split(bcc),
      subject,
      text: editor ? plainText(editor) : '',
      ...(body ? { html: body.html } : {}),
      attachments: [
        ...attachments.map(({ filename, contentType, data }) => ({
          filename,
          contentType,
          data,
        })),
        ...(body?.images ?? []).map(({ filename, contentType, data, contentId }) => ({
          filename,
          contentType,
          data,
          contentId,
        })),
      ],
      ...(initial.inReplyTo ? { inReplyTo: initial.inReplyTo } : {}),
      ...(initial.references ? { references: initial.references } : {}),
      ...(draftUid ? { draftUid } : {}),
      ...(sendAs ? { from: { address: sendAs.address, name: sendAs.name ?? '' } } : {}),
    };
  };

  /** The pictures in the body now, each once. */
  const shownPictures = (): InlineImage[] =>
    plain || !editor ? [] : pictures.shown(editor.getHTML());

  // Saved to Drafts 3 seconds after the last change, and at least every 15
  // seconds while the typing goes on. Closing with ✕ saves what is not yet
  // saved, and so does leaving the page. One save at a time, in order: each
  // replaces the draft the one before it made.
  const dirtyNow = useRef(0);
  dirtyNow.current = dirty;
  const latest = useRef(request);
  latest.current = request;
  const uidNow = useRef(draftUid);
  const savedUpTo = useRef(0);
  const pendingSince = useRef<number | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  // Discarded: nothing more is saved, and a save still on its way is undone.
  const closed = useRef(false);

  const saveNow = (): Promise<void> => {
    const upTo = dirtyNow.current;
    const body = (() => {
      const { draftUid: _previous, ...rest } = latest.current();
      return rest;
    })();
    queue.current = queue.current.then(async () => {
      if (closed.current || upTo <= savedUpTo.current) {
        return;
      }
      try {
        const uid = uidNow.current;
        const saved = await mail.saveDraft({ ...body, ...(uid ? { replaceUid: uid } : {}) });
        if (saved.uid) {
          uidNow.current = saved.uid;
          setDraftUid(saved.uid);
        }
        savedUpTo.current = Math.max(savedUpTo.current, upTo);
        setSavedAt(new Date());
      } catch {
        // Not saved: the next change, ✕ or leaving tries again.
      }
    });
    return queue.current;
  };

  useEffect(() => {
    if (dirty === 0 || dirty <= savedUpTo.current) {
      return;
    }
    pendingSince.current ??= Date.now();
    const wait = Math.max(0, Math.min(3000, pendingSince.current + 15_000 - Date.now()));
    const timer = window.setTimeout(() => {
      pendingSince.current = null;
      void saveNow();
    }, wait);
    return () => window.clearTimeout(timer);
    // The request is rebuilt from current state when the save runs.
  }, [dirty]);

  // Leaving the page (closing the tab, reloading, signing out): one last save,
  // sent so that it outlives the page.
  useEffect(() => {
    const leave = () => {
      if (!closed.current && dirtyNow.current > savedUpTo.current) {
        const { draftUid: _previous, ...body } = latest.current();
        const uid = uidNow.current;
        mail.saveDraftOnLeaving({ ...body, ...(uid ? { replaceUid: uid } : {}) });
      }
    };
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, []);

  /** ✕: closed, and what was written is in Drafts. */
  const closeKeepingDraft = () => {
    if (dirtyNow.current > savedUpTo.current) {
      void saveNow();
    }
    onClose();
  };

  const touch = () => setDirty((n) => n + 1);

  useEffect(() => {
    if (!tenant) {
      return;
    }
    const controller = new AbortController();
    api<AttachmentLinkPolicy>(`/tenants/${encodeURIComponent(tenant)}/attachment-links/policy`, {
      signal: controller.signal,
    })
      .then(setPolicy)
      // Unknown reads as off: files attach as before.
      .catch(() => undefined);
    return () => controller.abort();
  }, [tenant]);

  useEffect(() => {
    if (!tenant) {
      return;
    }
    const controller = new AbortController();
    api<{ addresses: SendAsAddress[] }>(`/tenants/${encodeURIComponent(tenant)}/send-as`, {
      signal: controller.signal,
    })
      .then((r) => setLent(Array.isArray(r.addresses) ? r.addresses : []))
      // None known: the person's own address, as before.
      .catch(() => undefined);
    return () => controller.abort();
  }, [tenant]);

  // Closing the composer stops any upload still going.
  useEffect(() => {
    const live = running.current;
    return () => live.forEach((upload) => upload.abort());
  }, []);

  /** The download link goes in the body, where the person can see and edit it. */
  const sendAsLink = (file: File) => {
    lastUpload.current += 1;
    const id = lastUpload.current;
    setUploads((u) => [...u, { id, filename: file.name, size: file.size, progress: 0 }]);
    const upload = uploadAttachmentLink(file, (progress) =>
      setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress } : x))),
    );
    running.current.set(id, upload);
    upload.done
      .then((link) => {
        if (editor && !editor.isDestroyed) {
          editor.chain().insertContentAt(linkPosition(editor), linkHtml(link)).run();
        }
      })
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        const reason = (e instanceof Error ? e.message : '').replace(/\.$/, '');
        setError(`Could not upload ${file.name}${reason ? `: ${reason}` : ''}.`);
      })
      .finally(() => {
        running.current.delete(id);
        setUploads((u) => u.filter((x) => x.id !== id));
      });
  };

  // From the person's storage (ADR-0049): a copy, scanned on its way here,
  // or a link to it, made only once the file has passed the scanner.
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<{ account: StorageAccount; entry: StorageEntry } | null>(
    null,
  );
  const [fetching, setFetching] = useState(false);
  const copyFromStorage = async () => {
    if (!picked || !tenant) {
      return;
    }
    setFetching(true);
    try {
      const file = await fetchForAttachment(
        tenant,
        picked.account.id,
        picked.entry.path,
        picked.entry.name,
      );
      setPicked(null);
      await add([file]);
    } catch (e) {
      setError(`${picked.entry.name}: ${e instanceof Error ? e.message : 'not attached'}.`);
    } finally {
      setFetching(false);
    }
  };
  const linkFromStorage = async () => {
    if (!picked || !tenant) {
      return;
    }
    setFetching(true);
    try {
      const link = await storage.link(tenant, picked.account.id, picked.entry.path);
      if (editor && !editor.isDestroyed) {
        const html =
          linkHtml({
            id: '',
            url: link.url,
            filename: link.name,
            size: link.size,
            expires_at: link.expires_at,
          }) + (link.password ? `<p>Password for the link: ${escape(link.password)}</p>` : '');
        editor.chain().insertContentAt(linkPosition(editor), html).run();
        touch();
      }
      setPicked(null);
    } catch (e) {
      setError(`${picked.entry.name}: ${e instanceof Error ? e.message : 'no link was made'}.`);
    } finally {
      setFetching(false);
    }
  };
  const copyFits =
    picked !== null &&
    (picked.entry.size ?? 0) + attachments.reduce((n, a) => n + a.size, 0) <= MAX_ATTACHMENTS;

  const add = async (list: FileList | readonly File[] | null) => {
    if (!list || list.length === 0) {
      return;
    }
    const incoming = [...list];
    const threshold = policy?.enabled ? policy.threshold_bytes : null;
    const asLinks = threshold === null ? [] : incoming.filter((f) => f.size > threshold);
    const attach = threshold === null ? incoming : incoming.filter((f) => f.size <= threshold);
    if (policy && asLinks.some((f) => f.size > policy.max_bytes)) {
      setError(`Files over ${Math.round(policy.max_bytes / 1024 ** 3)} GB can't be sent.`);
      return;
    }
    const total =
      attachments.reduce((n, a) => n + a.size, 0) +
      shownPictures().reduce((n, p) => n + p.size, 0) +
      attach.reduce((n, f) => n + f.size, 0);
    if (total > MAX_ATTACHMENTS) {
      setError('Attachments are limited to 25 MB together.');
      return;
    }
    setError(null);
    asLinks.forEach(sendAsLink);
    if (attach.length === 0) {
      return;
    }
    const added = await Promise.all(
      attach.map(async (f) => ({ ...(await read(f)), size: f.size })),
    );
    setAttachments((a) => [...a, ...added]);
    touch();
  };

  /**
   * Pictures pasted or dropped into the body go in where they were put, read
   * here and shown by a preview address; they count towards the same 20
   * files and 25 MB as attachments. Anything else dropped is attached, as
   * dropping on the window always did; anything else pasted is refused.
   */
  takeFiles.current = (files, at, how) => {
    if (!editor || editor.isDestroyed || (plain && how === 'drop')) {
      // A drop in plain text: the window attaches it, as before.
      return false;
    }
    const isPicture = (f: File) => PICTURE_TYPES.test(f.type);
    const others = files.filter((f) => !isPicture(f));
    const incoming = files.filter(isPicture);
    if (others.length && how === 'drop') {
      void add(others);
    } else if (others.length) {
      toast(
        `${others.map((f) => f.name || 'That').join(', ')} can't be pasted into the message: only pictures (PNG, JPEG, GIF or WebP) can. Attach other files with the paperclip.`,
        { tone: 'error' },
      );
    }
    if (incoming.length === 0) {
      return true;
    }
    if (plain) {
      void add(incoming);
      toast('A plain-text message has no pictures, so it is attached instead.');
      return true;
    }
    const shown = shownPictures();
    let room =
      MAX_ATTACHMENTS -
      attachments.reduce((n, a) => n + a.size, 0) -
      shown.reduce((n, p) => n + p.size, 0);
    const fits = incoming.filter((f) => {
      if (f.size > room) {
        toast(
          `${f.name || 'The picture'} (${size(f.size)}) is too large: attachments and pictures are limited to 25 MB together.`,
          { tone: 'error' },
        );
        return false;
      }
      room -= f.size;
      return true;
    });
    if (attachments.length + shown.length + fits.length > 20) {
      toast('A message can carry at most 20 files and pictures.', { tone: 'error' });
      return true;
    }
    const { from, to: end } = editor.state.selection;
    void Promise.all(
      fits.map(async (f) =>
        pictures.add(
          {
            ...(await read(f)),
            filename: f.name || `image.${f.type.slice(6)}`,
            contentType: f.type,
            size: f.size,
            contentId: newContentId(),
          },
          f,
        ),
      ),
    )
      .then((added) => {
        if (editor.isDestroyed || added.length === 0) {
          return;
        }
        // The body may have changed while the files were read.
        const max = editor.state.doc.content.size;
        editor
          .chain()
          .focus()
          .insertContentAt(
            at === null ? { from: Math.min(from, max), to: Math.min(end, max) } : Math.min(at, max),
            added.map((p) => ({ type: 'image', attrs: { src: p.url, alt: p.filename } })),
          )
          .run();
      })
      .catch(() => toast('That picture could not be read.', { tone: 'error' }));
    return true;
  };

  const uploading = uploads.length > 0;

  const [later, setLater] = useState(false);
  const [pick, setPick] = useState('');

  // Templates: messages in the Templates folder, shared with Thunderbird.
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState<readonly MessageSummary[] | null>(null);
  const [templateNote, setTemplateNote] = useState<string | null>(null);
  const toggleTemplates = () => {
    const opening = !templatesOpen;
    setTemplatesOpen(opening);
    setTemplateNote(null);
    if (opening) {
      setTemplates(null);
      mail
        .messages(TEMPLATES)
        .then((page) => setTemplates(page.messages))
        .catch(() => setTemplates([]));
    }
  };
  const applyTemplate = (uid: string) => {
    mail
      .message(TEMPLATES, uid)
      .then((t) => {
        const html = t.html ?? textToHtml(t.text ?? '');
        editor
          ?.chain()
          .focus()
          .insertContent(plain ? html : pictures.localise(html))
          .run();
        if (!subject.trim()) {
          setSubject(t.subject);
        }
        setTemplatesOpen(false);
        touch();
      })
      .catch(() => setTemplateNote('That template could not be opened.'));
  };
  const saveTemplate = () => {
    if (!subject.trim()) {
      setTemplateNote('Give it a subject first — that is its name.');
      return;
    }
    const { draftUid: _draft, ...body } = request();
    mail
      .saveTemplate({ ...body, to: [] })
      .then(() => setTemplateNote(`Saved “${subject.trim()}” as a template.`))
      .catch(() => setTemplateNote('The template was not saved. Try again.'));
  };

  const reopen = (): Draft => ({
    to,
    cc,
    bcc,
    subject,
    text: '',
    html: editor?.getHTML() ?? '',
    // Their previews stay on this page, so Undo shows them again.
    images: shownPictures(),
    ...(initial.inReplyTo ? { inReplyTo: initial.inReplyTo } : {}),
    ...(initial.references ? { references: initial.references } : {}),
    ...(draftUid ? { draftUid } : {}),
    ...(initial.original ? { original: initial.original } : {}),
    attachments,
  });

  const schedule = (at: Date) => {
    if (uploading) {
      setError(WAIT);
      return;
    }
    const req = request();
    if (req.to.length + (req.cc?.length ?? 0) + (req.bcc?.length ?? 0) === 0) {
      setError('Add at least one recipient.');
      return;
    }
    if (at.getTime() < Date.now() + 60_000) {
      setError('Choose a time at least a minute from now.');
      return;
    }
    setLater(false);
    onSchedule(req, at, reopen());
  };

  /** 08:00 on the next morning, and on the coming Monday. */
  const presets = (() => {
    const at = (days: number) => {
      const d = new Date();
      d.setDate(d.getDate() + days);
      d.setHours(8, 0, 0, 0);
      return d;
    };
    const tomorrow = at(1);
    const monday = at((8 - new Date().getDay()) % 7 || 7);
    const list: Array<[string, Date]> = [['Tomorrow morning', tomorrow]];
    if (monday.toDateString() !== tomorrow.toDateString()) {
      list.push(['Monday morning', monday]);
    }
    return list;
  })();

  // The organisation's data protection check, between Send and sending.
  const [checking, setChecking] = useState(false);
  const [stopped, setStopped] = useState<{
    check: DlpStop;
    go: () => void;
  } | null>(null);

  const send = () => {
    if (checking || stopped) {
      return;
    }
    if (uploading) {
      setError(WAIT);
      return;
    }
    const req = request();
    if (req.to.length + (req.cc?.length ?? 0) + (req.bcc?.length ?? 0) === 0) {
      setError('Add at least one recipient.');
      return;
    }
    const go = () => onSend(req, reopen());
    if (!tenant) {
      go();
      return;
    }
    setChecking(true);
    checkDraft(tenant, {
      to: req.to,
      cc: req.cc ?? [],
      bcc: req.bcc ?? [],
      subject: req.subject,
      text: req.text,
      attachments: (req.attachments ?? []).map((a) => a.filename),
    })
      .then((check) => {
        if (needsDecision(check)) {
          setStopped({ check, go });
        } else {
          go();
        }
      })
      // The mail store checks again as it sends: a failed check never stops a send.
      .catch(() => go())
      .finally(() => setChecking(false));
  };

  /**
   * The assistant's draft goes where the person writes, above the signature
   * and any quoted original, and stays editable. Their own text is replaced
   * only if they agree.
   */
  const insertDraft = (text: string, revised: boolean): boolean => {
    if (!editor) {
      return false;
    }
    const own = ownTextEnd(editor);
    // A revision is the person's own text reworked at their request: no asking.
    if (
      !revised &&
      own.text.trim() &&
      !window.confirm('Replace what you have written with this draft?')
    ) {
      return false;
    }
    editor
      .chain()
      .insertContentAt({ from: 0, to: own.pos }, textToHtml(text) + (own.bounded ? '<p></p>' : ''))
      .focus('start')
      .run();
    return true;
  };

  if (mode === 'min') {
    return (
      <div className="compose-sheet min" role="dialog" aria-label="New message, minimised">
        <button type="button" className="compose-min-bar" onClick={() => setMode('normal')}>
          {subject || 'New message'}
        </button>
        <button type="button" className="icon-btn" aria-label="Close" onClick={closeKeepingDraft}>
          <CloseIcon />
        </button>
      </div>
    );
  }

  const form = (
    <form
      className={`compose-sheet${mode === 'max' ? ' max' : ''}${over ? ' drop' : ''}`}
      role="dialog"
      aria-label="New message"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
          e.preventDefault();
          send();
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        // Not when the editor took it: a picture dropped into the body.
        if (e.dataTransfer.files.length && !e.defaultPrevented) {
          e.preventDefault();
          void add(e.dataTransfer.files);
        }
      }}
    >
      <div className="compose-head">
        <strong>{subject || 'New message'}</strong>
        <span className="compose-status">
          {savedAt
            ? `Draft saved ${savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : ''}
        </span>
        <button
          type="button"
          className="icon-btn"
          aria-label="Minimise"
          title="Minimise"
          onClick={() => setMode('min')}
        >
          <MinimizeIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={mode === 'max' ? 'Restore size' : 'Full size'}
          title={mode === 'max' ? 'Restore size' : 'Full size'}
          onClick={() => setMode(mode === 'max' ? 'normal' : 'max')}
        >
          <ExpandIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close"
          title="Close — kept in Drafts"
          onClick={closeKeepingDraft}
        >
          <CloseIcon />
        </button>
      </div>
      <div className="cmp-from">
        From{' '}
        {lent.length === 0 ? (
          <span className="addr">{from}</span>
        ) : (
          <select
            className="cmp-from-pick"
            aria-label="Send from"
            value={sendAs?.address ?? ''}
            onChange={(e) => setSendAs(lent.find((l) => l.address === e.target.value) ?? null)}
          >
            <option value="">{from}</option>
            {lent.map((l) => (
              <option key={l.address} value={l.address}>
                {l.name ? `${l.name} <${l.address}>` : l.address}
              </option>
            ))}
          </select>
        )}
        {!showCc && (
          <button type="button" className="link-btn" onClick={() => setShowCc(true)}>
            Cc and Bcc
          </button>
        )}
      </div>
      <AddressField
        tenant={tenant}
        label="To"
        value={to}
        onChange={(v) => {
          setTo(v);
          touch();
        }}
        autoFocus={!initial.to}
      />
      {showCc && (
        <>
          <AddressField
            tenant={tenant}
            label="Cc"
            value={cc}
            onChange={(v) => {
              setCc(v);
              touch();
            }}
          />
          <AddressField
            tenant={tenant}
            label="Bcc"
            value={bcc}
            onChange={(v) => {
              setBcc(v);
              touch();
            }}
          />
        </>
      )}
      <div className="cmp-field">
        <label>
          <span>Subject</span>
          <input
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              touch();
            }}
            aria-label="Subject"
          />
        </label>
      </div>
      {tenant && (
        <AiStrip
          tenant={tenant}
          senderName={senderName}
          original={initial.original}
          currentDraft={() => (editor ? ownTextEnd(editor).text : '')}
          onDraft={insertDraft}
        />
      )}
      {!plain && <Toolbar editor={editor} />}
      <EditorContent editor={editor} className="editor" />
      {(attachments.length > 0 || uploading) && (
        <ul className="attachments compose-atts">
          {attachments.map((a, i) => (
            <li key={`${a.filename}-${i}`} className="attachment">
              <PaperclipIcon />
              <span className="att-name">{a.filename}</span>
              <span className="att-size">{size(a.size)}</span>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Remove ${a.filename}`}
                onClick={() => {
                  setAttachments((all) => all.filter((_, j) => j !== i));
                  touch();
                }}
              >
                <CloseIcon />
              </button>
            </li>
          ))}
          {uploads.map((u) => (
            <li key={`upload-${u.id}`} className="attachment" aria-busy="true">
              <PaperclipIcon />
              <span className="att-name">{u.filename}</span>
              <span className="att-size">{size(u.size)}</span>
              <span className="att-size" role="status">
                Uploading… {Math.floor(u.progress * 100)}%
              </span>
              <button
                type="button"
                className="text-btn"
                aria-label={`Cancel uploading ${u.filename}`}
                onClick={() => running.current.get(u.id)?.abort()}
              >
                Cancel
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="compose-foot">
        <button
          type="submit"
          className="btn send-btn"
          title={uploading ? 'Waiting for the upload to finish' : 'Send (⌘/Ctrl + Enter)'}
          disabled={uploading || checking}
          aria-busy={checking || undefined}
        >
          <SendIcon /> Send
        </button>
        <span className="later">
          <button
            type="button"
            className="icon-btn"
            aria-label="Send later"
            title="Send later"
            aria-expanded={later}
            onClick={() => setLater((v) => !v)}
          >
            <ClockIcon />
          </button>
          {later && (
            <div className="later-menu" role="menu" aria-label="Send later">
              <strong>Send later</strong>
              {presets.map(([label, at]) => (
                <button key={label} type="button" role="menuitem" onClick={() => schedule(at)}>
                  <span>{label}</span>
                  <span className="muted">
                    {at.toLocaleString([], {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </button>
              ))}
              <label className="later-pick">
                <span>Choose a date and time</span>
                <input
                  type="datetime-local"
                  value={pick}
                  onChange={(e) => setPick(e.target.value)}
                />
              </label>
              <button
                type="button"
                className="btn"
                style={{ width: 'auto' }}
                disabled={!pick}
                onClick={() => schedule(new Date(pick))}
              >
                Schedule
              </button>
            </div>
          )}
        </span>
        <span className="later">
          <button
            type="button"
            className="text-btn"
            aria-expanded={templatesOpen}
            onClick={toggleTemplates}
          >
            Templates
          </button>
          {templatesOpen && (
            <div className="later-menu" role="menu" aria-label="Templates">
              <strong>Templates</strong>
              {templates === null && <span className="muted">Loading…</span>}
              {templates?.length === 0 && (
                <span className="muted">None yet. Write one, then save it here.</span>
              )}
              {templates?.map((t) => (
                <button
                  key={t.uid}
                  type="button"
                  role="menuitem"
                  onClick={() => applyTemplate(t.uid)}
                >
                  <span>{t.subject || '(no subject)'}</span>
                </button>
              ))}
              <button
                type="button"
                className="btn"
                style={{ width: 'auto' }}
                onClick={saveTemplate}
              >
                Save this message as a template
              </button>
              {templateNote && <span role="status">{templateNote}</span>}
            </div>
          )}
        </span>
        <button
          type="button"
          className="icon-btn"
          aria-label="Attach files"
          title="Attach files, or drop them here"
          onClick={() => files.current?.click()}
        >
          <PaperclipIcon />
        </button>
        {tenant && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Attach from Files"
            title="Attach from Files: Seafile, Nextcloud, ownCloud"
            onClick={() => setPicking(true)}
          >
            <FilesIcon />
          </button>
        )}
        <input
          ref={files}
          type="file"
          multiple
          hidden
          aria-label="Files to attach"
          onChange={(e) => void add(e.target.files)}
        />
        {picked && (
          <span className="from-storage" role="group" aria-label={`Attach ${picked.entry.name}`}>
            <span className="from-storage-name">{picked.entry.name}</span>
            {copyFits && (
              <button
                type="button"
                className="text-btn"
                disabled={fetching}
                onClick={() => void copyFromStorage()}
              >
                Attach a copy
              </button>
            )}
            <button
              type="button"
              className="text-btn"
              disabled={fetching}
              onClick={() => void linkFromStorage()}
            >
              Send a link
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Cancel"
              disabled={fetching}
              onClick={() => setPicked(null)}
            >
              <CloseIcon />
            </button>
          </span>
        )}
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="icon-btn"
          aria-label="Discard draft"
          title="Discard draft"
          onClick={() => {
            // One tap would lose a long message: ask, unless there is nothing to lose.
            const something =
              subject.trim() ||
              to.trim() ||
              attachments.length > 0 ||
              (editor ? plainText(editor).trim() : '');
            if (something && !window.confirm('Discard this draft? It is deleted, not saved.')) {
              return;
            }
            closed.current = true;
            // After any save still on its way, so its draft goes too.
            void queue.current.then(() => {
              const uid = uidNow.current;
              return uid ? mail.discardDraft(uid).catch(() => undefined) : undefined;
            });
            onClose();
          }}
        >
          <TrashIcon />
        </button>
      </div>
    </form>
  );

  const picker = picking && tenant && (
    <FilePicker
      slug={tenant}
      title="Attach from Files"
      mode={{
        kind: 'file',
        onPick: (account, entry) => {
          setPicking(false);
          setError(null);
          setPicked({ account, entry });
        },
      }}
      onClose={() => setPicking(false)}
    />
  );

  const dialog = stopped && (
    <DlpDialog
      check={stopped.check}
      onBack={() => setStopped(null)}
      onSend={() => {
        setStopped(null);
        stopped.go();
      }}
    />
  );

  return (
    <>
      {mode === 'max' ? <div className="modal-scrim">{form}</div> : form}
      {dialog}
      {picker}
    </>
  );
}
